import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import type { FieldError } from "react-hook-form"

interface CInputMaskProps {
  value?: string | number | readonly string[] | null
  defaultValue?: string | number | readonly string[] | null
  onChange?: (val: number) => void
  onBlur?: (val: number) => void
  error?: FieldError | undefined
  className?: string
  disabled?: boolean
  format?: "hh:mm:ss" | "hh:mm" | "mm:ss"
  placeholder?: string
}

const MASK_CONFIG = {
  "mm:ss": {
    digitPositions: [0, 1, 3, 4],
    placeholder: "00:00",
    maskLength: 5,
  },
  "hh:mm": {
    digitPositions: [0, 1, 3, 4],
    placeholder: "00:00",
    maskLength: 5,
  },
  "hh:mm:ss": {
    digitPositions: [0, 1, 3, 4, 6, 7],
    placeholder: "00:00:00",
    maskLength: 8,
  },
} as const

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)
const pad = (value: number) => value.toString().padStart(2, "0")

const createNullDigits = (length: number) => Array.from({ length }, () => null as string | null)

export const CInputMask: React.FC<CInputMaskProps> = (props) => {
  const {
    value,
    defaultValue,
    onChange,
    onBlur,
    error,
    className,
    disabled,
    format = "hh:mm:ss",
    placeholder,
    ...rest
  } = props

  const { digitPositions, placeholder: maskPlaceholder, maskLength } = MASK_CONFIG[format]
  const digitsCount = digitPositions.length
  const placeholderChar = "0"

  const inputRef = useRef<HTMLInputElement | null>(null)
  const caretRef = useRef<number | null>(null)
  const skipBeforeInputRef = useRef(false)

  const [hasFocus, setHasFocus] = useState(false)
  const [digits, setDigits] = useState<(string | null)[]>(() => {
    const seconds = resolveInitialSeconds(value ?? defaultValue)
    if (seconds == null) {
      return createNullDigits(digitsCount)
    }
    return secondsToDigits(seconds, format)
  })

  // Follow the props while the field is not being edited: the render that sees a
  // new value, default, format or the focus leaving adopts them. Typing is left
  // alone until blur.
  const [syncedFrom, setSyncedFrom] = useState({ value, defaultValue, format, hasFocus })
  if (
    syncedFrom.value !== value ||
    syncedFrom.defaultValue !== defaultValue ||
    syncedFrom.format !== format ||
    syncedFrom.hasFocus !== hasFocus
  ) {
    setSyncedFrom({ value, defaultValue, format, hasFocus })
    if (!hasFocus && (value !== undefined || defaultValue !== undefined)) {
      setDigits((prev) => digitsFollowingProps(prev, value ?? defaultValue, format))
    }
  }

  const { t } = useTranslation()

  const placeholderValue = useMemo(() => {
    if (placeholder) return placeholder
    return format === "hh:mm:ss"
      ? t("common.timeFormat", { defaultValue: "00:00:00" })
      : t("common.paceFormat", { defaultValue: "00:00" })
  }, [format, placeholder, t])

  const formattedValue = useMemo(() => {
    const displayChars = digits.map((digit) => digit ?? placeholderChar)
    if (format === "hh:mm:ss") {
      return `${displayChars[0]}${displayChars[1]}:${displayChars[2]}${displayChars[3]}:${displayChars[4]}${displayChars[5]}`
    }
    return `${displayChars[0]}${displayChars[1]}:${displayChars[2]}${displayChars[3]}`
  }, [digits, format])

  const emitChange = useCallback(
    (nextDigits: (string | null)[]) => {
      const seconds = digitsToSeconds(nextDigits, format)
      onChange?.(seconds)
    },
    [format, onChange],
  )

  const scheduleCaretFromDigit = useCallback(
    (digitIndex: number) => {
      const caretPosition = digitIndexToCaret(digitIndex, digitPositions, maskLength)
      caretRef.current = caretPosition
    },
    [digitPositions, maskLength],
  )

  const updateDigits = useCallback(
    (mutate: (draft: (string | null)[]) => void, nextDigitIndex?: number) => {
      let updatedDraft: (string | null)[] = []
      setDigits((prev) => {
        const draft = [...prev]
        mutate(draft)
        updatedDraft = draft
        return draft
      })
      emitChange(updatedDraft)
      if (typeof nextDigitIndex === "number") {
        scheduleCaretFromDigit(nextDigitIndex)
      }
    },
    [emitChange, scheduleCaretFromDigit],
  )

  const getSelection = useCallback(() => {
    const input = inputRef.current
    if (!input) return { start: 0, end: 0 }
    return {
      start: clamp(input.selectionStart ?? 0, 0, maskLength),
      end: clamp(input.selectionEnd ?? 0, 0, maskLength),
    }
  }, [maskLength])

  const handleDigit = useCallback(
    (digit: string) => {
      if (!/^[0-9]$/.test(digit)) return
      const { start, end } = getSelection()
      const startIndex = caretToDigitIndex(start, digitPositions)
      const endIndex = caretToDigitIndex(end, digitPositions)
      if (startIndex >= digitsCount) return

      updateDigits((draft) => {
        for (let i = startIndex; i < endIndex; i += 1) {
          draft[i] = null
        }
        draft[startIndex] = digit
      }, startIndex + 1)
    },
    [digitPositions, digitsCount, getSelection, updateDigits],
  )

  const handleBackspace = useCallback(() => {
    const { start, end } = getSelection()
    const startIndex = caretToDigitIndex(start, digitPositions)
    const endIndex = caretToDigitIndex(end, digitPositions)

    if (startIndex !== endIndex) {
      updateDigits((draft) => {
        for (let i = startIndex; i < endIndex; i += 1) {
          draft[i] = null
        }
      }, startIndex)
      return
    }

    const targetIndex = startIndex - 1
    if (targetIndex < 0) return
    updateDigits((draft) => {
      draft[targetIndex] = null
    }, targetIndex)
  }, [digitPositions, getSelection, updateDigits])

  const handleDelete = useCallback(() => {
    const { start, end } = getSelection()
    const startIndex = caretToDigitIndex(start, digitPositions)
    const endIndex = caretToDigitIndex(end, digitPositions)

    if (startIndex !== endIndex) {
      updateDigits((draft) => {
        for (let i = startIndex; i < endIndex; i += 1) {
          draft[i] = null
        }
      }, startIndex)
      return
    }

    if (startIndex >= digitsCount) return
    updateDigits((draft) => {
      draft[startIndex] = null
    }, startIndex)
  }, [digitPositions, digitsCount, getSelection, updateDigits])

  const handlePaste = useCallback(
    (raw: string) => {
      if (!raw) return
      const digitsOnly = raw.replace(/\D/g, "")
      if (!digitsOnly) return
      const { start, end } = getSelection()
      const startIndex = caretToDigitIndex(start, digitPositions)
      const endIndex = caretToDigitIndex(end, digitPositions)

      updateDigits(
        (draft) => {
          for (let i = startIndex; i < endIndex; i += 1) {
            draft[i] = null
          }

          let current = startIndex
          for (const char of digitsOnly) {
            if (current >= digitsCount) break
            draft[current] = char
            current += 1
          }
        },
        Math.min(startIndex + digitsOnly.length, digitsCount),
      )
    },
    [digitPositions, digitsCount, getSelection, updateDigits],
  )

  const markSkipBeforeInput = useCallback(() => {
    skipBeforeInputRef.current = true
    requestAnimationFrame(() => {
      skipBeforeInputRef.current = false
    })
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    if (event.key === "Backspace") {
      event.preventDefault()
      markSkipBeforeInput()
      handleBackspace()
      return
    }

    if (event.key === "Delete") {
      event.preventDefault()
      markSkipBeforeInput()
      handleDelete()
      return
    }

    if (event.key === "ArrowLeft" && !event.shiftKey) {
      event.preventDefault()
      const { start } = getSelection()
      const index = caretToDigitIndex(start, digitPositions)
      const nextIndex = Math.max(index - 1, 0)
      const caret = digitIndexToCaret(nextIndex, digitPositions, maskLength)
      caretRef.current = caret
      inputRef.current?.setSelectionRange(caret, caret)
      return
    }

    if (event.key === "ArrowRight" && !event.shiftKey) {
      event.preventDefault()
      const { end } = getSelection()
      const index = caretToDigitIndex(end, digitPositions)
      const nextIndex = Math.min(index + 1, digitsCount)
      const caret = digitIndexToCaret(nextIndex, digitPositions, maskLength)
      caretRef.current = caret
      inputRef.current?.setSelectionRange(caret, caret)
      return
    }

    if ((event.ctrlKey || event.metaKey) && (event.key === "v" || event.key === "V")) {
      // allow paste handler to manage
      return
    }

    if (event.key.length === 1 && /[0-9]/.test(event.key)) {
      event.preventDefault()
      markSkipBeforeInput()
      handleDigit(event.key)
      return
    }

    if (event.key.length === 1 && /[^0-9]/.test(event.key)) {
      event.preventDefault()
    }
  }

  const handleBeforeInput = (event: React.SyntheticEvent<HTMLInputElement, InputEvent>) => {
    if (disabled) return
    if (skipBeforeInputRef.current) return

    const nativeEvent = event.nativeEvent
    const { inputType, data } = nativeEvent

    if (inputType === "insertText" && data) {
      event.preventDefault()
      handleDigit(data)
      return
    }

    if (inputType === "deleteContentBackward") {
      event.preventDefault()
      handleBackspace()
      return
    }

    if (inputType === "deleteContentForward") {
      event.preventDefault()
      handleDelete()
      return
    }

    if (inputType === "insertFromPaste" && data) {
      event.preventDefault()
      handlePaste(data)
    }
  }

  const handlePasteEvent = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return
    event.preventDefault()
    const pasted = event.clipboardData.getData("text")
    handlePaste(pasted)
  }

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (disabled) return
    setHasFocus(true)
    const target = event.currentTarget
    requestAnimationFrame(() => {
      if (!target.isConnected) return
      target.setSelectionRange(0, maskLength)
    })
  }

  const handleBlur = () => {
    if (disabled) return
    setHasFocus(false)

    const isEmpty = digits.every((digit) => digit == null)
    if (isEmpty) {
      onChange?.(0)
      onBlur?.(0)
      return
    }

    const seconds = digitsToSeconds(digits, format)
    const normalizedDigits = secondsToDigits(seconds, format)

    setDigits((prev) => {
      if (areDigitsEqual(prev, normalizedDigits)) return prev
      return normalizedDigits
    })

    onChange?.(seconds)
    onBlur?.(seconds)
  }

  useEffect(() => {
    if (caretRef.current == null) return
    const caret = caretRef.current
    caretRef.current = null
    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange(caret, caret)
    })
  }, [formattedValue, digits])

  return (
    <input
      ref={inputRef}
      inputMode="numeric"
      value={formattedValue}
      placeholder={placeholderValue || maskPlaceholder}
      disabled={disabled}
      onKeyDown={handleKeyDown}
      onBeforeInput={handleBeforeInput}
      onPaste={handlePasteEvent}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={() => {}}
      className={`border-1 rounded-lg h-[30px] px-1.5 w-full text-sm bg-white disabled:opacity-50 disabled:cursor-not-allowed ${error ? "border-red-300" : "border-gray-300"} ${className ?? ""}`}
      {...rest}
    />
  )
}

/** The digits for a value from props, keeping `prev` itself when nothing would change. */
const digitsFollowingProps = (
  prev: (string | null)[],
  raw: CInputMaskProps["value"],
  format: NonNullable<CInputMaskProps["format"]>,
): (string | null)[] => {
  const seconds = resolveInitialSeconds(raw)
  if (seconds == null) {
    return prev.every((digit) => digit == null) ? prev : createNullDigits(MASK_CONFIG[format].digitPositions.length)
  }
  const nextDigits = secondsToDigits(seconds, format)
  return areDigitsEqual(prev, nextDigits) ? prev : nextDigits
}

const resolveInitialSeconds = (raw: CInputMaskProps["value"]): number | null => {
  if (raw === null || raw === undefined || raw === "") return null

  // Handle array case: extract first element
  if (Array.isArray(raw)) {
    const stringArray = raw as readonly string[]
    const firstElement: string = stringArray[0] ?? ""
    if (firstElement === "") return null
    const numeric = Number(firstElement)
    return Number.isFinite(numeric) ? Math.max(0, Math.floor(numeric)) : null
  }

  // Handle number case
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : null
  }

  // Handle string case
  const numeric = Number(raw)
  return Number.isFinite(numeric) ? Math.max(0, Math.floor(numeric)) : null
}

const digitsToSeconds = (source: (string | null)[], format: "hh:mm:ss" | "hh:mm" | "mm:ss") => {
  if (format === "mm:ss") {
    const minutes = clamp(parseInt(`${source[0] ?? "0"}${source[1] ?? "0"}`, 10) || 0, 0, 59)
    const seconds = clamp(parseInt(`${source[2] ?? "0"}${source[3] ?? "0"}`, 10) || 0, 0, 59)
    return minutes * 60 + seconds
  }

  if (format === "hh:mm") {
    const hours = clamp(parseInt(`${source[0] ?? "0"}${source[1] ?? "0"}`, 10) || 0, 0, 23)
    const minutes = clamp(parseInt(`${source[2] ?? "0"}${source[3] ?? "0"}`, 10) || 0, 0, 59)
    return hours * 3600 + minutes * 60
  }

  const hours = clamp(parseInt(`${source[0] ?? "0"}${source[1] ?? "0"}`, 10) || 0, 0, 99)
  const minutes = clamp(parseInt(`${source[2] ?? "0"}${source[3] ?? "0"}`, 10) || 0, 0, 59)
  const seconds = clamp(parseInt(`${source[4] ?? "0"}${source[5] ?? "0"}`, 10) || 0, 0, 59)
  return hours * 3600 + minutes * 60 + seconds
}

const secondsToDigits = (totalSeconds: number, format: "hh:mm:ss" | "hh:mm" | "mm:ss") => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds))
  if (format === "mm:ss") {
    const minutes = clamp(Math.floor(safeSeconds / 60), 0, 59)
    const seconds = clamp(safeSeconds % 60, 0, 59)
    return `${pad(minutes)}${pad(seconds)}`.split("")
  }

  if (format === "hh:mm") {
    const hours = clamp(Math.floor(safeSeconds / 3600), 0, 23)
    const minutes = clamp(Math.floor((safeSeconds / 60) % 60), 0, 59)
    return `${pad(hours)}${pad(minutes)}`.split("")
  }

  const hours = clamp(Math.floor(safeSeconds / 3600), 0, 99)
  const minutes = clamp(Math.floor((safeSeconds / 60) % 60), 0, 59)
  const seconds = clamp(safeSeconds % 60, 0, 59)
  return `${pad(hours)}${pad(minutes)}${pad(seconds)}`.split("")
}

const caretToDigitIndex = (caret: number, digitPositions: readonly number[]) => {
  const index = digitPositions.findIndex((position) => caret <= position)
  return index === -1 ? digitPositions.length : index
}

const digitIndexToCaret = (digitIndex: number, digitPositions: readonly number[], maskLength: number) => {
  if (digitIndex <= 0) return 0
  if (digitIndex >= digitPositions.length) return maskLength
  return digitPositions[digitIndex] ?? maskLength
}

const areDigitsEqual = (a: (string | null)[], b: (string | null)[]) => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false
  }
  return true
}
